import type { Asset } from '@schibsted-svp/svp-api-types';
import { FunctionComponent, createContext } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';

import { useApis } from '../ApisProvider';
import { useConfig } from '../ConfigProvider';
import { useCurrentAsset } from '../CurrentAssetProvider';
import { usePlayer } from '../PlayerProvider';

import { getNextAssets } from './getNextAssets';

export interface NextAssetContextProps {
  nextAsset?: Asset;
  nextAssets: Asset[];
}

const NextAssetContext = createContext<NextAssetContextProps>(undefined!);

export const NextAssetProvider: FunctionComponent = ({ children }) => {
  const player = usePlayer();
  const { relatedApi, playlistApi } = useApis();
  const { asset } = useCurrentAsset();
  const config = useConfig();

  const [nextAssets, setNextAssets] = useState<Asset[]>([]);

  useEffect(() => {
    getNextAssets({
      asset,
      relatedApi,
      playlistApi,
      config,
    })
      .then((assets) => {
        setNextAssets(assets);
      })
      .catch((error) => {
        console.error('Failed to fetch next assets', error);
      });
  }, [asset, relatedApi, playlistApi, config]);

  const state = useMemo(() => {
    return {
      nextAsset: nextAssets[0],
      nextAssets,
    };
  }, [nextAssets]);

  const { nextAsset } = state;

  useEffect(() => {
    if (nextAsset) {
      player.trigger('VgtvSkinPlugin:nextAssetReady', nextAsset);
    }
  }, [player, nextAsset]);

  return (
    <NextAssetContext.Provider value={state}>
      {children}
    </NextAssetContext.Provider>
  );
};

export const useNextAsset = () => {
  const nextAsset = useContext(NextAssetContext);

  if (process.env.NODE_ENV !== 'production' && !nextAsset) {
    console.warn(
      'Next asset context is undefined, please verify you are calling useNextAsset() as child of a <NextAssetProvider> component.'
    );
  }

  return nextAsset;
};
